import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LoadingState,
  Toggle,
  WorkspaceBody,
  WorkspaceHeader,
  WorkspacePage,
  useToast,
} from '../ui/WorkspaceUI';
import { parseQueryFields } from '../../utils/mailSearchQuery';
import { describeActions, draftFromFilter, emptyDraft } from '../../utils/mailFilters';
import FilterEditor from './FilterEditor';

/**
 * Smart filters: rules applied automatically to incoming mail.
 *
 * Order matters — filters run top to bottom and one can stop the rest — so the
 * list is explicitly ordered and reorderable rather than sorted by name or date.
 *
 * A `?q=` parameter pre-fills the conditions, which is how "create a filter from
 * this search" hands a query over from the search results page.
 */
const FiltersManager = ({ currentUser }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axiosInstance.get('/api/filters');
      setFilters(data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load your filters');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Opening with ?q= starts a new filter from that search, then drops the
  // parameter so a refresh doesn't reopen the form.
  useEffect(() => {
    const query = searchParams.get('q');
    if (!query) return;
    setDraft({ ...emptyDraft(), fields: parseQueryFields(query) });
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadLog = async () => {
    try {
      const { data } = await axiosInstance.get('/api/filters/log', { params: { limit: 50 } });
      setLog(data.data || []);
    } catch (err) {
      toast('Could not load filter history', { tone: 'error' });
    }
  };

  const toggleLog = () => {
    const next = !showLog;
    setShowLog(next);
    if (next) loadLog();
  };

  const save = async (payload) => {
    setSaving(true);
    try {
      if (draft._id) {
        await axiosInstance.put(`/api/filters/${draft._id}`, payload);
        toast('Filter updated', { tone: 'success' });
      } else {
        await axiosInstance.post('/api/filters', payload);
        toast('Filter created — it will run on new mail', { tone: 'success' });
      }
      setDraft(null);
      await load();
    } catch (err) {
      toast(err.response?.data?.message || 'Could not save the filter', { tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const setEnabled = async (filter, enabled) => {
    // Optimistic: a switch that lags feels broken. Reverted by `load` on failure.
    setFilters((prev) => prev.map((item) => (item._id === filter._id ? { ...item, enabled } : item)));
    try {
      await axiosInstance.put(`/api/filters/${filter._id}`, { enabled });
    } catch (err) {
      toast('Could not change the filter', { tone: 'error' });
      load();
    }
  };

  const remove = async () => {
    const filter = pendingDelete;
    setPendingDelete(null);
    try {
      await axiosInstance.delete(`/api/filters/${filter._id}`);
      toast('Filter deleted', { tone: 'success' });
      await load();
    } catch (err) {
      toast('Could not delete the filter', { tone: 'error' });
    }
  };

  const move = async (index, delta) => {
    const next = [...filters];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setFilters(next);
    try {
      await axiosInstance.put('/api/filters/reorder', { ids: next.map((item) => item._id) });
    } catch (err) {
      toast('Could not save the new order', { tone: 'error' });
      load();
    }
  };

  const applyToExisting = async (filter) => {
    try {
      const { data } = await axiosInstance.post(`/api/filters/${filter._id}/apply`, { limit: 200 });
      toast(data.message, { tone: 'success' });
      await load();
    } catch (err) {
      toast(err.response?.data?.message || 'Could not apply the filter', { tone: 'error' });
    }
  };

  return (
    <WorkspacePage>
      <WorkspaceHeader
        icon="🧲"
        title="Filters"
        subtitle="Rules applied automatically to mail as it arrives"
      >
        <Button variant="ghost" onClick={toggleLog}>
          {showLog ? 'Hide history' : 'History'}
        </Button>
        <Button variant="primary" onClick={() => setDraft(emptyDraft())} disabled={!!draft}>
          New filter
        </Button>
      </WorkspaceHeader>

      <WorkspaceBody>
        {draft && (
          <div className="mb-4">
            <FilterEditor
              draft={draft}
              onChange={setDraft}
              onSave={save}
              onCancel={() => setDraft(null)}
              saving={saving}
              ownerEmail={currentUser?.email}
            />
          </div>
        )}

        {loading && <LoadingState label="Loading filters…" />}
        {!loading && error && <ErrorState message={error} onRetry={load} />}

        {!loading && !error && filters.length === 0 && !draft && (
          <EmptyState
            icon="🧲"
            title="No filters yet"
            description="Filters can label, archive, star, forward or delete mail the moment it arrives. Conditions use the same syntax as search."
            action={
              <Button variant="primary" onClick={() => setDraft(emptyDraft())}>
                Create a filter
              </Button>
            }
          />
        )}

        {!loading && !error && filters.length > 0 && (
          <ol className="space-y-2">
            {filters.map((filter, index) => (
              <li key={filter._id} className="ws-glass rounded-xl px-3 py-2">
                <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
                  <span className="mt-1 w-5 shrink-0 text-center text-xs text-ink-soft" aria-hidden>
                    {index + 1}
                  </span>

                  <div className="min-w-0 flex-1 basis-64">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate text-sm font-semibold text-ink">
                        {filter.name || filter.query}
                      </h3>
                      {!filter.enabled && <Badge tone="neutral">Off</Badge>}
                      {filter.matchCount > 0 && (
                        <Badge tone="neutral">matched {filter.matchCount}×</Badge>
                      )}
                    </div>
                    <code className="mt-1 block break-words font-mono text-xs text-accent">
                      {filter.query}
                    </code>
                    <p className="mt-1 text-xs text-ink-soft">{describeActions(filter.actions)}</p>
                  </div>

                  <div className="w-32 shrink-0">
                    <Toggle
                      checked={filter.enabled}
                      onChange={(value) => setEnabled(filter, value)}
                      label="On"
                    />
                  </div>

                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      onClick={() => move(index, -1)}
                      disabled={index === 0}
                      aria-label="Run earlier"
                      title="Run earlier"
                    >
                      ↑
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => move(index, 1)}
                      disabled={index === filters.length - 1}
                      aria-label="Run later"
                      title="Run later"
                    >
                      ↓
                    </Button>
                    <Button variant="ghost" onClick={() => setDraft(draftFromFilter(filter))}>
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => applyToExisting(filter)}
                      title="Run this filter over mail already in your mailbox"
                    >
                      Run now
                    </Button>
                    <Button variant="ghost" onClick={() => setPendingDelete(filter)}>
                      Delete
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}

        {showLog && (
          <section className="mt-6">
            <h2 className="text-sm font-semibold text-ink">Recent filter activity</h2>
            <p className="mt-1 text-xs text-ink-soft">
              What your filters have done, newest first. Kept for 90 days.
            </p>
            {log.length === 0 ? (
              <p className="mt-3 text-xs text-ink-soft">No filter has fired yet.</p>
            ) : (
              <ul className="mt-3 space-y-1">
                {log.map((entry) => (
                  <li
                    key={`${entry.messageKey}-${entry.filterId}-${entry.appliedAt}`}
                    className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-hairline py-1.5 text-xs"
                  >
                    <span className="shrink-0 text-ink-soft">
                      {new Date(entry.appliedAt).toLocaleString()}
                    </span>
                    <span className="shrink-0 font-medium text-ink">{entry.filterName}</span>
                    <span className="min-w-0 flex-1 truncate text-ink-soft">
                      {entry.from} — {entry.subject || '(no subject)'}
                    </span>
                    <span className="shrink-0 font-mono text-accent">{entry.actions.join(', ')}</span>
                    {entry.error && (
                      <span className="shrink-0 text-red-600 dark:text-red-400">{entry.error}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </WorkspaceBody>

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete this filter?"
        message={
          pendingDelete
            ? `"${pendingDelete.name || pendingDelete.query}" will stop running. Mail it already changed stays as it is.`
            : ''
        }
        confirmLabel="Delete"
        tone="danger"
        onConfirm={remove}
        onClose={() => setPendingDelete(null)}
      />
    </WorkspacePage>
  );
};

export default FiltersManager;
